import { memoize } from 'lodash';
import type { Point, PointOpacity } from '../store';

// Import WASM module statically so Vite can process it
import decodeWasmInit from '../wasm/decode.wasm?init';

// Dynamic WASM loading - Vite handles browser, we handle Node.js
async function loadWasmModule(imports: WebAssembly.Imports): Promise<WebAssembly.Instance> {
  // Detect if we're running in Node.js or browser
  const isNode =
    typeof process !== 'undefined' && process.versions != null && process.versions.node != null;

  if (isNode) {
    // Node.js: Load WASM from filesystem
    // Use globalThis to access require without Vite's static analysis transforming it
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const nodeRequire = (globalThis as { require?: NodeRequire }).require ?? require;
    const fs = nodeRequire('node:fs') as typeof import('node:fs');
    const path = nodeRequire('node:path') as typeof import('node:path');

    // Use __dirname in CJS or construct path from cwd for ts-node/vitest
    let currentDir: string;
    if (typeof __dirname !== 'undefined') {
      currentDir = __dirname;
    } else {
      // Fallback for ESM - use process.cwd() and navigate to the file location
      currentDir = path.join(process.cwd(), 'src', 'lib', 'player', 'utils');
    }
    const wasmPath = path.join(currentDir, '..', 'wasm', 'decode.wasm');

    const wasmBuffer = fs.readFileSync(wasmPath);
    const wasmModule = await WebAssembly.compile(wasmBuffer);
    const instance = await WebAssembly.instantiate(wasmModule, imports);
    return instance;
  }

  // Browser: Use the statically imported WASM init function
  return await decodeWasmInit(imports);
}

const UINT32_SIZE = Uint32Array.BYTES_PER_ELEMENT;
const FLOAT32_SIZE = Float32Array.BYTES_PER_ELEMENT;
const FLOAT64_SIZE = Float64Array.BYTES_PER_ELEMENT;
const MONGOID_SIZE = 12;

const mongoIdToUint32Array = memoize(function mongoIdToUint32Array(mongoId: string): Uint32Array {
  const mongoIdUint32Length = MONGOID_SIZE / UINT32_SIZE;
  const mongoIdUint32 = new Uint32Array(mongoIdUint32Length);
  for (let j = 0; j < mongoIdUint32Length; j++) {
    mongoIdUint32[j] = Number.parseInt(
      mongoId.substring(j * 2 * UINT32_SIZE, (j + 1) * 2 * UINT32_SIZE),
      16,
    );
  }
  return mongoIdUint32;
});

interface DebugCoordinate {
  starterId: string;
  lat: number;
  lng: number;
  time: number;
  offset: number;
  isOnTrack: boolean;
}
export type DebugCoordinates = Array<DebugCoordinate>;

type CalculateSizeFunc = (
  in_ptr: number,
  in_size: number,
  track_ids_ptr: number,
  track_ids_size: number,
) => number;
type DecodeToBufferFunc = (
  in_ptr: number,
  in_size: number,
  track_ids_ptr: number,
  track_ids_size: number,
  out_ptr: number,
  out_size: number,
) => void;
type InterpolateFunc = (
  in_ptr: number,
  in_size: number,
  time: number,
  marker_timeout: number,
  out_ptr: number,
  out_size: number,
) => void;
type InterpolateOffsetsFunc = (
  in_ptr: number,
  in_size: number,
  shadow_track_ptr: number,
  shadow_track_size: number,
  time: number,
  marker_timeout: number,
  is_magnetic: boolean,
  jump_speed_threshold: number,
  out_ptr: number,
  out_size: number,
) => void;

class Reader {
  dataView: DataView;
  constructor(buffer: ArrayBufferLike, byteOffset = 0, byteLength = -1) {
    if (byteLength === -1) {
      this.dataView = new DataView(buffer, byteOffset);
    } else {
      this.dataView = new DataView(buffer, byteOffset, byteLength);
    }
  }

  readMongoId(i: number): string {
    let out = '';
    for (let j = 0; j < 12; j++) {
      out += this.dataView
        .getUint8(i + j)
        .toString(16)
        .padStart(2, '0');
    }
    return out;
  }
  readUint8(i: number): number {
    return this.dataView.getUint8(i);
  }
  readUint32(i: number): number {
    return this.dataView.getUint32(i, true);
  }
  readUint32BE(i: number): number {
    return this.dataView.getUint32(i, false);
  }
  readFloat32(i: number): number {
    return this.dataView.getFloat32(i, true);
  }
  readFloat64(i: number): number {
    return this.dataView.getFloat64(i, true);
  }
  sub(byteOffset: number, byteLength: number): Reader {
    return new Reader(this.dataView.buffer, this.dataView.byteOffset + byteOffset, byteLength);
  }
  export(): ArrayBufferLike {
    return this.dataView.buffer.slice(
      this.dataView.byteOffset,
      this.dataView.byteOffset + this.dataView.byteLength,
    );
  }
}

class DecodedTrackLayout {
  static POINT_SIZE = FLOAT64_SIZE * 5;
  reader: Reader;

  constructor(reader: Reader) {
    this.reader = reader;
  }

  static from(buffer: ArrayBuffer, byteOffset: number, pointCount: number): DecodedTrackLayout {
    return new DecodedTrackLayout(
      new Reader(buffer, byteOffset, pointCount * DecodedTrackLayout.POINT_SIZE),
    );
  }

  get pointCount(): number {
    return this.reader.dataView.byteLength / DecodedTrackLayout.POINT_SIZE;
  }

  getLat(i: number): number {
    return this.reader.readFloat64(i * DecodedTrackLayout.POINT_SIZE + 0 * FLOAT64_SIZE);
  }
  getLng(i: number): number {
    return this.reader.readFloat64(i * DecodedTrackLayout.POINT_SIZE + 1 * FLOAT64_SIZE);
  }
  getTimestamp(i: number): number {
    return this.reader.readFloat64(i * DecodedTrackLayout.POINT_SIZE + 2 * FLOAT64_SIZE);
  }
  getTime(i: number): Date {
    return new Date(this.getTimestamp(i));
  }
  getOffset(i: number): number {
    return this.reader.readFloat64(i * DecodedTrackLayout.POINT_SIZE + 3 * FLOAT64_SIZE);
  }
  getIsOnTrack(i: number): boolean {
    return this.reader.readUint8(i * DecodedTrackLayout.POINT_SIZE + 4 * FLOAT64_SIZE) === 1;
  }
  getPoint(i: number): Point & { isOnTrack: boolean } {
    return {
      lat: this.getLat(i),
      lng: this.getLng(i),
      time: this.getTimestamp(i),
      offset: this.getOffset(i),
      isOnTrack: this.getIsOnTrack(i),
    };
  }
  exportPoints(): Array<Point> {
    const output = [];
    for (let i = 0; i < this.pointCount; i++) {
      output.push(this.getPoint(i));
    }
    return output;
  }
}

export class LZ4DecodedChunkLayout {
  static HEADER_SIZE = 2 * UINT32_SIZE;
  static HEADER_ITEM_SIZE = 12 + 2 * UINT32_SIZE;

  reader: Reader;
  constructor(reader: Reader) {
    this.reader = reader;
    this.findTrackIndex = memoize(this.findTrackIndex);
  }

  static from(buffer: ArrayBufferLike, byteOffset = 0, byteLength = -1): LZ4DecodedChunkLayout {
    return new LZ4DecodedChunkLayout(new Reader(buffer, byteOffset, byteLength));
  }

  get trackCount(): number {
    return this.reader.readUint32(0);
  }
  get stepWidth(): number {
    return this.reader.readUint32(4);
  }
  getStarterId(i: number): string {
    return this.reader.readMongoId(
      LZ4DecodedChunkLayout.HEADER_SIZE + i * LZ4DecodedChunkLayout.HEADER_ITEM_SIZE,
    );
  }
  getTrackOffset(i: number): number {
    return this.reader.readUint32(
      LZ4DecodedChunkLayout.HEADER_SIZE + i * LZ4DecodedChunkLayout.HEADER_ITEM_SIZE + 12,
    );
  }
  getTrackLength(i: number): number {
    return this.reader.readUint32(
      LZ4DecodedChunkLayout.HEADER_SIZE + i * LZ4DecodedChunkLayout.HEADER_ITEM_SIZE + 16,
    );
  }
  getTrack(i: number): DecodedTrackLayout {
    return new DecodedTrackLayout(
      this.reader.sub(
        this.getTrackOffset(i),
        this.getTrackLength(i) * DecodedTrackLayout.POINT_SIZE,
      ),
    );
  }
  getTrackByStarterId(starterId: string): DecodedTrackLayout {
    const trackIndex = this.findTrackIndex(starterId);
    return this.getTrack(trackIndex);
  }
  compareStarterId(i: number, starterIdUint32: Uint32Array): boolean {
    const starterIdOffset =
      LZ4DecodedChunkLayout.HEADER_SIZE + i * LZ4DecodedChunkLayout.HEADER_ITEM_SIZE;
    return (
      this.reader.readUint32BE(starterIdOffset) === starterIdUint32[0] &&
      this.reader.readUint32BE(starterIdOffset + UINT32_SIZE) === starterIdUint32[1] &&
      this.reader.readUint32BE(starterIdOffset + 2 * UINT32_SIZE) === starterIdUint32[2]
    );
  }
  findTrackIndex(starterId: string): number {
    const starterIdUint32 = mongoIdToUint32Array(starterId);
    for (let i = 0; i < this.trackCount; i++) {
      if (this.compareStarterId(i, starterIdUint32)) {
        return i;
      }
    }
    return -1;
  }
  export(): ArrayBufferLike {
    return this.reader.export();
  }
  exportMap(): Map<string, Array<Point>> {
    const output = new Map();
    for (let i = 0; i < this.trackCount; i++) {
      output.set(this.getStarterId(i), this.getTrack(i).exportPoints());
    }
    return output;
  }
  debug(): DebugCoordinates {
    const output = [];
    for (let i = 0; i < this.trackCount; i++) {
      const starterId = this.getStarterId(i);
      const track = this.getTrack(i);
      for (let j = 0; j < track.pointCount; j++) {
        const p = track.getPoint(j);
        // p.time = new Date(p.time);
        output.push({ starterId, ...p });
      }
    }
    return output;
  }
}

export class RenderInfoLayout {
  static POSITION_ITEM_STRIDE = 6;
  static POSITION_ITEM_SIZE = FLOAT32_SIZE * RenderInfoLayout.POSITION_ITEM_STRIDE;
  static INFO_ITEM_STRIDE = 3;
  static INFO_ITEM_SIZE = FLOAT64_SIZE * RenderInfoLayout.INFO_ITEM_STRIDE;
  static ITEM_SIZE = RenderInfoLayout.POSITION_ITEM_SIZE + RenderInfoLayout.INFO_ITEM_SIZE;

  positions: Float32Array;
  infoItems: Float64Array;
  _count: number;

  constructor(positions: Float32Array, infoItems: Float64Array) {
    this.positions = positions;
    this.infoItems = infoItems;
    this._count = positions.length / RenderInfoLayout.POSITION_ITEM_STRIDE;
  }

  static from(buffer: ArrayBuffer, byteOffset: number, byteLength: number): RenderInfoLayout {
    const count = byteLength / RenderInfoLayout.ITEM_SIZE;
    return new RenderInfoLayout(
      new Float32Array(buffer, byteOffset, count * RenderInfoLayout.POSITION_ITEM_STRIDE),
      new Float64Array(
        buffer,
        byteOffset + RenderInfoLayout.POSITION_ITEM_SIZE * count,
        count * RenderInfoLayout.INFO_ITEM_STRIDE,
      ),
    );
  }

  static fromCopied(buffer: ArrayBuffer, byteOffset: number, byteLength: number): RenderInfoLayout {
    const newBuffer = buffer.slice(byteOffset, byteOffset + byteLength);
    return RenderInfoLayout.from(newBuffer, 0, byteLength);
  }

  getLat(i: number): number {
    return this.getLat0(i) + this.getLat1(i);
  }
  getLng(i: number): number {
    return this.getLng0(i) + this.getLng1(i);
  }
  getLat0(i: number): number {
    return this.positions[i * RenderInfoLayout.POSITION_ITEM_STRIDE + 1];
  }
  getLng0(i: number): number {
    return this.positions[i * RenderInfoLayout.POSITION_ITEM_STRIDE + 0];
  }
  getLat1(i: number): number {
    return this.positions[i * RenderInfoLayout.POSITION_ITEM_STRIDE + 4];
  }
  getLng1(i: number): number {
    return this.positions[i * RenderInfoLayout.POSITION_ITEM_STRIDE + 3];
  }
  getTimestamp(i: number): number {
    return this.infoItems[i * RenderInfoLayout.INFO_ITEM_STRIDE + 0];
  }
  getTime(i: number): Date {
    return new Date(this.getTimestamp(i));
  }
  getOffset(i: number): number {
    return this.infoItems[i * RenderInfoLayout.INFO_ITEM_STRIDE + 1];
  }
  getOpacity(i: number): number {
    return this.infoItems[i * RenderInfoLayout.INFO_ITEM_STRIDE + 2];
  }
  get(i: number): PointOpacity {
    return {
      lat: this.getLat(i),
      lng: this.getLng(i),
      time: this.getTimestamp(i),
      offset: this.getOffset(i),
      opacity: this.getOpacity(i),
    };
  }
}

type MemoryLayoutItem = { key: string; ptr: number; size: number };
class MemoryLayout {
  static ALLOC_ALIGN = 8;

  memory: WebAssembly.Memory;
  heapBase: number;
  layout: Map<string, MemoryLayoutItem>;
  constructor(memory: WebAssembly.Memory, heapBase: number | WebAssembly.Global) {
    this.memory = memory;
    this.heapBase = typeof heapBase === 'number' ? heapBase : heapBase.value;
    this.layout = new Map();
  }

  get(key: string): MemoryLayoutItem {
    const entry = this.layout.get(key);
    if (entry == null) {
      throw new Error(`Key=${key} is missing from memory layout.`);
    }
    return entry;
  }

  getBuffer(): ArrayBuffer {
    return this.memory.buffer;
  }

  copyBuffer(ptr: number, buffer: ArrayBufferLike): void {
    new Uint8Array(this.memory.buffer).set(new Uint8Array(buffer), ptr);
  }

  allocAndCopyBuffer(key: string, buffer: ArrayBufferLike): void {
    const entry = this.alloc(key, buffer.byteLength);
    this.copyBuffer(entry.ptr, buffer);
  }

  alloc(key: string, size: number): MemoryLayoutItem {
    let entry = this.layout.get(key);
    if (entry != null) {
      if (entry.size !== size) {
        // Remove all subsequent memory layout items
        for (const [keyOther, entryOther] of this.layout.entries()) {
          if (entryOther.ptr >= entry.ptr) {
            this.layout.delete(keyOther);
          }
        }
      } else {
        return entry;
      }
    }
    let ptr = this.byteLength();
    if (ptr % MemoryLayout.ALLOC_ALIGN !== 0) {
      ptr += MemoryLayout.ALLOC_ALIGN - (ptr % MemoryLayout.ALLOC_ALIGN);
    }

    entry = { key, ptr, size };
    this.layout.set(key, entry);
    this.growMemoryTo(this.byteLength());
    return entry;
  }

  reset(key: string): void {
    const entry = this.layout.get(key);
    if (entry != null) {
      this.layout.delete(key);
      for (const [otherKey, otherEntry] of this.layout) {
        if (otherEntry.ptr >= entry.ptr) {
          this.layout.delete(otherKey);
        }
      }
    }
  }

  growMemoryTo(byteLength: number): void {
    const pageCountBefore = this.memory.buffer.byteLength >>> 16;
    const pageCountAfter = (byteLength + 65535) >>> 16;
    if (pageCountAfter > pageCountBefore) {
      this.memory.grow(pageCountAfter - pageCountBefore);
    }
  }

  byteLength(): number {
    return Array.from(this.layout.values()).reduce(
      (r, entry) => Math.max(r, entry.ptr + entry.size),
      this.heapBase,
    );
  }
}

export class LZ4Decoder {
  static MEMORY_SHADOW_TRACK = 'MEMORY_SHADOW_TRACK';
  static MEMORY_TRACK_IDS = 'MEMORY_TRACK_IDS';
  static MEMORY_ENCODED_CHUNK = 'MEMORY_ENCODED_CHUNK';
  static MEMORY_DECODED_CHUNK = 'MEMORY_DECODED_CHUNK';

  instance: WebAssembly.Instance;
  memoryLayout: MemoryLayout;
  currentDecodedChunk: ArrayBufferLike | null = null;

  constructor(instance: WebAssembly.Instance) {
    this.instance = instance;
    this.memoryLayout = new MemoryLayout(
      instance.exports.memory as WebAssembly.Memory,
      instance.exports.__heap_base as WebAssembly.Global,
    );
  }
  importShadowTrack(buffer: ArrayBufferLike): void {
    this.memoryLayout.allocAndCopyBuffer(LZ4Decoder.MEMORY_SHADOW_TRACK, buffer);
  }
  resetShadowTrack(): void {
    this.memoryLayout.reset(LZ4Decoder.MEMORY_SHADOW_TRACK);
  }
  importRawChunk(buffer: ArrayBufferLike): void {
    this.memoryLayout.allocAndCopyBuffer(LZ4Decoder.MEMORY_ENCODED_CHUNK, buffer);
  }
  resetRawChunk(): void {
    this.memoryLayout.reset(LZ4Decoder.MEMORY_ENCODED_CHUNK);
  }
  importDecodedChunk(buffer: ArrayBufferLike): void {
    this.memoryLayout.allocAndCopyBuffer(LZ4Decoder.MEMORY_DECODED_CHUNK, buffer);
  }
  resetDecodedChunk(): void {
    this.memoryLayout.reset(LZ4Decoder.MEMORY_DECODED_CHUNK);
  }

  updateDecodedChunk(buffer: ArrayBufferLike): void {
    if (this.currentDecodedChunk !== buffer) {
      this.resetDecodedChunk();
      this.importDecodedChunk(buffer);
      this.currentDecodedChunk = buffer;
    }
  }
  importStarterIds(buffer: ArrayBufferLike): void {
    this.memoryLayout.allocAndCopyBuffer(LZ4Decoder.MEMORY_TRACK_IDS, buffer);
  }
  resetStarterIds(): void {
    this.memoryLayout.reset(LZ4Decoder.MEMORY_TRACK_IDS);
  }
  decodeRawChunk(): LZ4DecodedChunkLayout {
    const starterIdsEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_TRACK_IDS);
    const rawEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_ENCODED_CHUNK);
    const trackSize = (this.instance.exports.CalculateSize as CalculateSizeFunc)(
      rawEntry.ptr,
      rawEntry.size,
      starterIdsEntry.ptr,
      starterIdsEntry.size,
    );
    const trackEntry = this.memoryLayout.alloc(LZ4Decoder.MEMORY_DECODED_CHUNK, trackSize);
    (this.instance.exports.DecodeToBuffer as DecodeToBufferFunc)(
      rawEntry.ptr,
      rawEntry.size,
      starterIdsEntry.ptr,
      starterIdsEntry.size,
      trackEntry.ptr,
      trackEntry.size,
    );

    return LZ4DecodedChunkLayout.from(
      this.memoryLayout.getBuffer(),
      trackEntry.ptr,
      trackEntry.size,
    );
  }
  interpolate(time: number, markerTimeout: number): RenderInfoLayout {
    const starterIdsEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_TRACK_IDS);
    const trackEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_DECODED_CHUNK);
    const entryCount = starterIdsEntry.size / MONGOID_SIZE;
    const outputEntry = this.memoryLayout.alloc(
      'interpolated',
      entryCount * RenderInfoLayout.ITEM_SIZE,
    );
    (this.instance.exports.Interpolate as InterpolateFunc)(
      trackEntry.ptr,
      trackEntry.size,
      time,
      markerTimeout,
      outputEntry.ptr,
      outputEntry.size,
    );

    return RenderInfoLayout.fromCopied(
      this.memoryLayout.getBuffer(),
      outputEntry.ptr,
      outputEntry.size,
    );
  }
  interpolateOffsets(
    time: number,
    markerTimeout: number,
    is_magnetic: boolean,
    jumpSpeedThreshold: number,
  ): RenderInfoLayout {
    const starterIdsEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_TRACK_IDS);
    const trackEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_DECODED_CHUNK);
    const shadowEntry = this.memoryLayout.get(LZ4Decoder.MEMORY_SHADOW_TRACK);
    const entryCount = starterIdsEntry.size / MONGOID_SIZE;
    const outputEntry = this.memoryLayout.alloc(
      'interpolated',
      entryCount * RenderInfoLayout.ITEM_SIZE,
    );
    (this.instance.exports.InterpolateOffsets as InterpolateOffsetsFunc)(
      trackEntry.ptr,
      trackEntry.size,
      shadowEntry.ptr,
      shadowEntry.size,
      time,
      markerTimeout,
      is_magnetic,
      jumpSpeedThreshold,
      outputEntry.ptr,
      outputEntry.size,
    );
    return RenderInfoLayout.fromCopied(
      this.memoryLayout.getBuffer(),
      outputEntry.ptr,
      outputEntry.size,
    );
  }
}

export async function createDecoder(): Promise<LZ4Decoder> {
  const imports = {
    env: {
      __print_date(num: number) {
        // eslint-disable-next-line no-console
        console.log('__print_date', new Date(num));
      },
      __print_num(num: number) {
        // eslint-disable-next-line no-console
        console.log('__print_num', num);
      },
      __print_num2(a: number, b: number) {
        // eslint-disable-next-line no-console
        console.log('__print_num2', a, b);
      },
      __print_num4(a: number, b: number, c: number, d: number) {
        // eslint-disable-next-line no-console
        console.log('__print_num4', a, b, c, d);
      },
      __throw_out_of_bounds(ptr: number, size: number, index: number) {
        throw new Error(`Index out of bounds. ptr=${ptr} size=${size} index=${index}`);
      },
      memory: new WebAssembly.Memory({ initial: 100 }),
      table: new WebAssembly.Table({
        initial: 64,
        element: 'anyfunc',
      }),
    },
  };
  const instance = await loadWasmModule(imports);

  return new LZ4Decoder(instance);
}
